import * as yup from "yup";
import { useInfiniteQuery } from "@tanstack/react-query";
import { HTTP_ENDPOINTS, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";
import { removeNullishFromShape, unwrapOr } from "../../utilities/general";
import {
  composePaginationSchema,
  getNextPaginationPage,
  paginationSchema,
} from "../../schemas/pagination";

const requestSchema = yup.object({
  pageSize: yup.number().required(),
});

const responseSchema = paginationSchema.concat(
  yup.object({
    reports: yup
      .array()
      .of(
        yup.object({
          date: yup.string().required(),
          patientName: yup.string().required(),
          productName: yup.string().nullable(),
          unitsCount: yup.number().required(),
          dollarValue: yup.string().required(),
          balanceUnitsCount: yup.number().required(),
          walletBalanceDollarValue: yup.string().required(),
          balanceServiceValue: yup.string().required(),
        }),
      )
      .required(),

    stats: yup
      .object({
        total: yup.string().nullable(),
      })
      .required(),
  }),
);

const composeResponse = (res, req) => {
  const reports = unwrapOr(() => res.data.data.data, []);
  return {
    ...composePaginationSchema({
      response: unwrapOr(() => res.data.data, {}),
      pageSize: req.pageSize,
    }),
    reports: reports.map((report) => ({
      date: report.date,
      patientName: report.client,
      productName: report.product_name,
      unitsCount: report.units || 0,
      dollarValue: report.dollar_value,
      balanceUnitsCount: report.balance_units || 0,
      invoiceId: report.invoice_id,
      packageId: report.package_id,
      patientId: report.patient_id,
      walletBalanceDollarValue: report.wallet_balance_dollar_value,
      balanceServiceValue: report.balance_service_value,
    })),
    stats: {
      total: res?.data?.data?.total_liability || null,
    },
  };
};

function createSalesExpiringLiabilityReportQueryKey({ pageSize }) {
  return [QUERY_KEYS.expiredLiabilityReport, pageSize];
}

export function useSalesExpiringLiabilityReportQuery({
  payload = {},
  options = {},
} = {}) {
  return useInfiniteQuery({
    queryKey: createSalesExpiringLiabilityReportQueryKey({
      pageSize: payload.pageSize,
    }),
    queryFn: async ({ pageParam = 1 }) => {
      const req = requestSchema.validateSync(payload, {
        strict: true,
      });
      const res = await http.post(
        HTTP_ENDPOINTS.createExpiringLiabilityReport(),
        removeNullishFromShape({
          page: pageParam,
          pagesize: req.pageSize,
        }),
      );
      return responseSchema.validateSync(composeResponse(res, req), {
        strict: true,
      });
    },
    ...options,
    getNextPageParam: getNextPaginationPage,
  });
}
